import { TdcTransactionModel, TransactionDataToDisplay } from '@3ddv/utils-igsu';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, catchError, from, mergeMap, of, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CartService } from '../../_shared/data-access/cart.service';
import { ModalService } from '../../_shared/data-access/modal.service';
import { SeatsService } from '../../_shared/data-access/seats.service';
import { SectionsService } from '../../_shared/data-access/sections.service';
import { SessionService } from '../../_shared/data-access/session.service';
import { TimerService } from '../../_shared/data-access/timer.service';
import { getMessageError } from '../../_shared/utils/get-message-error';
import { secondsToMiliseconds } from '../../_shared/utils/seconds-to-miliseconds';
import { EventsTicketsService } from '../../events/data-access/events-tickets.service';
import { EventsService } from '../../events/data-access/events.service';
import { PriceScalesService } from '../../events/data-access/price-scales.service';
import { PaymentAPI } from './_payment.api';
import { BraintreeService } from './braintree.service';
import { StripeService } from './stripe.service';

declare const gtag: (arg0: string, arg1: string, arg2: Record<string, unknown>) => void;

/**
 * PaymentService is an injectable service in charge of managing the payment process within an application.
 * It interacts with various APIs and services to create, monitor, and cancel payment transactions,
 * navigate upon successful or unsuccessful payments, and provide the current status of the payment process.
 * It leverages BehaviorSubjects to emit current transaction and payment success status.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private _api = inject(PaymentAPI);
  private _router = inject(Router);
  private _injector = inject(Injector);
  private _modalService = inject(ModalService);
  private _timerService = inject(TimerService);
  private _eventService = inject(EventsService);
  private _sessionService = inject(SessionService);
  private _cartService = inject(CartService);
  private _seatsService = inject(SeatsService);
  private _sectionsService = inject(SectionsService);
  private _priceScalesService = inject(PriceScalesService);
  private _eventTicketsService = inject(EventsTicketsService);

  private _transaction = new BehaviorSubject<TransactionDataToDisplay | null>(null);
  public transaction$ = this._transaction.asObservable();

  private _isPaymentSuccessful: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isPaymentSuccessful$: Observable<boolean> = this._isPaymentSuccessful.asObservable();

  public isPaymentAccepted = false;
  public errorMessage = '';
  public mmcRequestCode = '';
  public purchaseData: {
    price: number;
    oldPrice: number;
    newPrice: number;
    row: string;
    seat: string;
    section: string;
  }[] = [];

  private seatSelectionRoute = '/seat-selection';
  private _routeToNavigate = '/summary';

  /**
   * Initializes the payment process by creating a payment transaction and starting a timer.
   * @returns A promise that resolves after initializing the payment or returns null if preconditions aren't met.
   */
  public initialize = async (): Promise<Subscription | undefined> => {
    if (this.getTransaction() != null) return;

    const returnedTicketsData = this._eventTicketsService.getReturnTicketsData();
    const priceScaleId = this._priceScalesService.getSelectedPriceScale()!;
    const sectionData = this._sectionsService.getSelectedSectionData();
    const selectedSeats = this._seatsService.getSelectedSeatsData()!;
    const eventId = this._eventService.getEventConfiguration()?.eventId;
    const oktaId = this._sessionService.getPatron()!.oktaId;
    const hasSeats =
      this._eventService.getEventConfiguration()?.seatMap! && !this._sectionsService.isCurrentSectionGeneralAdmission();

    if (!returnedTicketsData?.length || !sectionData) return;

    const createPaymentParams = {
      returnedTicketsData,
      priceScaleId,
      sectionData,
      selectedSeats,
      eventId,
      oktaId,
      hasSeats,
    };

    return this._api
      .createPayment(createPaymentParams)
      .pipe(catchError(this.onPaymentCreationError))
      .subscribe(this.onPaymentCreationSuccess);
  };

  public onPaymentProviderError = (err: string) => {
    const config = {
      type: 'app',
      text: 'There is no existing cart for this transaction. Please restart the operation.',
      buttonText: 'Go back to seat selection',
      buttonAction: () => {
        this._modalService.closeModal();
        this._router.navigate([this.seatSelectionRoute]);
      },
    };
    this._modalService.openModal(config);
    return of(undefined);
  };

  /**
   * Callback function executed when payment creation is successful.
   * Sets the transaction data, calculates timer duration, and starts the timer.
   * @param data The created transaction data.
   */
  private onPaymentCreationSuccess = (data: void | TransactionDataToDisplay) => {
    if (!data) return;

    this.setTransaction(data);
    this._cartService.setTotalPrice(data.balance);
    const miliseconds = secondsToMiliseconds(data.cartTimer);
    return this._timerService.startTimer(miliseconds);
  };

  /**
   * Handles errors during payment creation by displaying a relevant error message to the user.
   * Depending on the error type, it suggests different actions to the user.
   *
   * @param err - The error object containing the error description.
   */
  private onPaymentCreationError = (err: { error: { code: string; message: string } }) => {
    const config = this.getErrorConfiguration(err)!;
    this._modalService.openModal(config);
    return of(undefined); // Returns an Observable that emits 'undefined' as a signal of completion.
  };

  /**
   * Determines the appropriate configuration for the modal based on the error message.
   *
   * @param  errorMessage - The error message received from the error object.
   * @returns An object containing the modal configuration.
   */
  private getErrorConfiguration(err: { error?: { code: string; message: string } }) {
    const formattedErrorMessage = getMessageError(err.error!);
    const navigateRoute = formattedErrorMessage?.includes('exchangeable') ? '/events' : '/seat-selection';
    let buttonMessage = 'Go back';

    if (formattedErrorMessage.includes('unexpected')) {
      buttonMessage = 'Restart application';
    }

    return {
      type: 'app',
      text: formattedErrorMessage!,
      buttonText: buttonMessage,
      buttonAction: () => {
        this._modalService.closeModal();

        if (formattedErrorMessage.includes('unexpected')) {
          location.reload();
        } else {
          this._router.navigate([navigateRoute]);
        }
      },
    };
  }

  /**
   * Retrieves the current transaction data.
   * @returns The current transaction data.
   */
  public getTransaction = (): TdcTransactionModel => {
    return this._transaction.getValue()!;
  };

  /**
   * @returns Whether the transaction exists or not
   */
  public isExistingTransaction = () => {
    return !!this._transaction.getValue();
  };

  /**
   * Sets the transaction data and updates the observable.
   * @param transaction The transaction data to set.
   */
  public setTransaction = (transaction: TransactionDataToDisplay) => {
    this._transaction.next(transaction);
  };

  /**
   * Resets the transaction to null
   */
  public resetTransaction = () => {
    this._transaction.next(null);
  };

  /**
   * Navigates to a predefined route upon successful payment.
   */
  public navigateOnPayment = () => {
    this._router.navigate([this._routeToNavigate]);
  };

  /**
   * Marks the payment as successful.
   */
  public setSuccessfulPayment = () => {
    this._isPaymentSuccessful.next(true);
  };

  /**
   * Marks the payment as unsuccessful.
   */
  public setUnsuccesfulPayment = () => {
    this._isPaymentSuccessful.next(false);
  };

  /**
   * Reset timer observable and transaction value
   */
  public resetPayment = () => {
    this._timerService.resetTimer();
    this.resetTransaction();
  };

  public resetComponentInstance = () => {
    if (this._eventService.getEventConfiguration()?.paymentMethod == 'stripe') {
      const stripeService = this._injector.get(StripeService);
      stripeService.resetStripeComponent();
    } else {
      const braintreeService = this._injector.get(BraintreeService);
      braintreeService.resetBraintreeComponent();
    }
  };

  /**
   * Prepares state to proceed with checkout success
   */
  public handlePaymentSuccess = () => {
    this.setSuccessfulPayment();
    this._timerService.resetTimer();
    this._priceScalesService.unselectPriceScale();
    this._eventTicketsService.unselectAllTickets();
    this.resetComponentInstance();

    const transaction = this.getTransaction();
    const client_id = this._sessionService.getPatron()?.environment;

    if (environment?.production && environment?.analytics?.mmc) {
      gtag('event', 'purchase', {
        transaction_id: transaction?.cartId,
        value: transaction.balance,
        currency: 'USD',
        client_id,
        items: this.purchaseData,
      });
    }

    this.resetTransaction();

    this._router.navigate(['summary']);
  };

  /**
   * Prepares state to proceed with checkout fail
   */
  public handlePaymentFail = () => {
    this.setUnsuccesfulPayment();
    this._router.navigate(['summary']);
  };

  /**
   * Cancels the HTTP request related to the current payment transaction.
   */
  public cancelHttpPaymentRequest = () => {
    const transaction = this.getTransaction();
    if (!transaction) return;

    this._api.cancelPayment(transaction).pipe(take(1)).subscribe(this.resetTransaction);
  };

  /**
   * Handles the cancellation of the checkout process by canceling the payment,
   * closing modal, navigating back, and resetting the timer.
   */
  public onCheckoutCanceled = () => {
    this.cancelHttpPaymentRequest();

    const timerService = this._injector.get(TimerService);
    this._modalService.closeModal();
    this._router.navigate([this.seatSelectionRoute]);
    this.resetPayment();
    timerService.isCheckoutCanceled = true;
  };

  /**
   * Initiates the checkout process with the obtained nonce from the payment service component.
   * @param token The token representing the payment method obtained from the payment service component.
   * @returns An Observable that emits the checkout process result.
   */
  public checkoutOnRequest = (token: string, cardType?: string) => {
    return from([token]).pipe(
      mergeMap(token => {
        const { cartId, transactionId } = this.getTransaction();
        const eventConfiguration = this._eventService.getEventConfiguration();
        const paymentProvider = eventConfiguration?.paymentMethod;
        const externalPaymentMethodId = eventConfiguration?.externalPaymentMethodId;

        if (!cartId || !transactionId || !paymentProvider) {
          console.error('Implement handler if this is not defined');
          return of(false);
        }

        return this._api.checkout({ paymentProvider, cartId, transactionId, token, cardType, externalPaymentMethodId });
      }),
      catchError(err => {
        this.mmcRequestCode = err.headers.get('3ddv-Request-Id');
        return throwError(err);
      })
    );
  };
}
